import { Box, Grid2 } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { ADMINISTRATION_ROUTES } from "../../pages/administration/constants";
import { userRoleValidationRules } from "../../pages/administration/validation";
import { RolesService, SubscriptionService } from "../../services";
import { ActionButton, CardLayout, InputField } from "../v3";
import { ModuleFeatureSelector } from "./ModuleFeatureSelector";
import type { Feature } from "./ModuleFeatureSelector";
import { MODULE_CONFIG, enforceGlobalDependencies, enforceGlobalSelectAllRules } from "./moduleGroupConfigs";
import type { SelectedPermission } from "./moduleGroupConfigs";

interface Module {
  id: number;
  code: string;
  name: string;
  children: Feature[];
}

interface RoleDetails {
  name?: string;
  description?: string;
  roleFeatures: { featureId: number; active: boolean }[];
}

interface RootState {
  user: {
    permissions: string[];
  };
}

interface AddEditUserRoleFormProps {
  roleId?: string | number;
}

export const AddEditUserRoleForm: React.FC<AddEditUserRoleFormProps> = ({ roleId }) => {
  const [availableModules, setAvailableModules] = useState<Module[]>([]);
  const [allModules, setAllModules] = useState<Module[]>([]);
  const [selectedPermissions, setSelectedPermissions] = useState<SelectedPermission[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [roleDetails, setRoleDetails] = useState<RoleDetails>({
    name: undefined,
    description: undefined,
    roleFeatures: [],
  });

  const { permissions: currentUserPermissions } = useSelector((state: RootState) => state.user);
  const hiddenFeatureCodes: string[] = useSelector((state: any) => {
    const limits = state.user?.user?.subscription?.limits;
    if (!limits) return [];
    return Object.values(limits).flatMap((limit: any) => limit?.hiddenFeatureCodes || []);
  });
  const hiddenFeatureCodesMemo = React.useMemo(
    () => hiddenFeatureCodes,
    [hiddenFeatureCodes.length, hiddenFeatureCodes.join(",")],
  );
  const navigate = useNavigate();

  useEffect(() => {
    fetchModules();

    if (roleId) {
      fetchRoleData();
    }
  }, [currentUserPermissions, hiddenFeatureCodesMemo, roleId]);

  const fetchModules = async () => {
    try {
      const { data: modulesData } = await SubscriptionService.getAllModules();
      setAllModules(modulesData);
      // Filter modules and features based on current user permissions, excluding hidden features
      const filteredModules = modulesData
        .filter((module: Module) => currentUserPermissions.includes(module.code))
        .map((module: Module) => ({
          ...module,
          children: module.children.filter(
            (child: Feature) => currentUserPermissions.includes(child.code) && !hiddenFeatureCodesMemo.includes(child.code),
          ),
        }))
        .filter((module: Module) => module.children.length > 0);
      setAvailableModules(filteredModules);
    } catch (error) {
      console.error("Error fetching modules:", error);
    }
  };

  const fetchRoleData = async () => {
    try {
      const { data: roleData } = await RolesService.getRole(roleId);

      const features = roleData.roleFeatures
        ?.filter((roleFeature: any) => {
          const code = roleFeature.feature?.code;
          return !hiddenFeatureCodesMemo.includes(code);
        })
        .map((roleFeature: any) => ({
          id: roleFeature.featureId,
          code: roleFeature.feature.code,
          active: roleFeature.active,
        }));
      setSelectedPermissions(features || []);

      // Set role details
      setRoleDetails({
        name: roleData?.name || "",
        description: roleData?.description || "",
        roleFeatures: roleData?.roleFeatures.map((roleFeature: any) => ({
          featureId: roleFeature.featureId,
          active: roleFeature.active,
        })),
      });
    } catch (error) {
      toast.error("Error fetching role details");
      console.error("Error:", error);
    }
  };

  const updateDetailsState = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setRoleDetails({ ...roleDetails, [name]: value });
  };

  const handleSelectAll = () => {
    const activePermissions = availableModules.flatMap((module) =>
      module.children.map((feature) => ({ ...feature, active: true })),
    );
    setSelectedPermissions(
      enforceGlobalDependencies(enforceGlobalSelectAllRules(activePermissions), hiddenFeatureCodesMemo),
    );
  };

  const handleClearAll = () => {
    const clearedPermissions = availableModules.flatMap((module) =>
      module.children.map((feature) => ({ ...feature, active: false })),
    );
    setSelectedPermissions(clearedPermissions);
  };

  const handleSaveRole = async () => {
    setErrors({});
    const validationErrors = userRoleValidationRules(roleDetails);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const hiddenFeatures = allModules.flatMap((module: Module) =>
      module.children.filter((child: Feature) => hiddenFeatureCodesMemo.includes(child.code)),
    );

    const roleFeaturesFromSelected = selectedPermissions.map((permission) => ({
      featureId: permission.id,
      active: permission.active,
    }));

    // Append hidden features as active:true, skipping any already present to avoid duplicates
    hiddenFeatures.forEach((feature: Feature) => {
      if (!roleFeaturesFromSelected.find((rf) => rf.featureId === feature.id)) {
        roleFeaturesFromSelected.push({ featureId: feature.id, active: true });
      }
    });

    const payload = {
      name: roleDetails.name,
      description: roleDetails.description,
      roleFeatures: roleFeaturesFromSelected,
    };

    try {
      setLoading(true);
      if (roleId) {
        await RolesService.updateRole(roleId, payload);
      } else {
        await RolesService.createRole(payload);
      }
      toast.success(`Role ${roleId ? "updated" : "added"} successfully`);
      navigate(ADMINISTRATION_ROUTES.root, { state: { activeTab: "roles" } });
    } catch (error) {
      toast.error(`Error ${roleId ? "updating" : "adding"} role`);
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box sx={{ position: "relative", height: 40 }}>
        <CardLayout
          sx={{
            position: "absolute",
            bottom: "-20px",
            left: "0px",
            right: "0px",
          }}
        >
          <Grid2 container spacing={2} sx={{ alignItems: "flex-start" }}>
            <Grid2 size={9.5} sx={{ display: "flex", gap: 1 }}>
              <InputField
                required
                label="Role Name"
                name="name"
                fullWidth
                value={roleDetails.name}
                onChange={updateDetailsState}
                error={!!errors.name}
                helperText={errors.name}
              />
              <InputField
                label="Description"
                name="description"
                fullWidth
                value={roleDetails.description}
                onChange={updateDetailsState}
              />
            </Grid2>
            <Grid2
              size={2.5}
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 1,
              }}
            >
              <ActionButton
                buttonText="Clear All"
                variant="outlined"
                onButtonClick={handleClearAll}
                sx={{ textTransform: "capitalize" }}
              />
              <ActionButton buttonText="Select All" onButtonClick={handleSelectAll} sx={{ textTransform: "capitalize" }} />
            </Grid2>
          </Grid2>
        </CardLayout>
      </Box>

      <CardLayout>
        <Grid2 container spacing={2}>
          {MODULE_CONFIG.map((moduleConfig) => {
            const module = availableModules.find((m) => m.code === moduleConfig.code);
            if (!module?.children?.length) return null;
            return (
              <ModuleFeatureSelector
                key={moduleConfig.code}
                config={moduleConfig}
                moduleFeatures={module.children}
                selectedPermissions={selectedPermissions}
                setSelectedPermissions={setSelectedPermissions}
                hiddenFeatureCodes={hiddenFeatureCodesMemo}
              />
            );
          })}
        </Grid2>
        <Grid2 size={4} sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1 }}>
          <ActionButton
            buttonText="close"
            variant="outlined"
            onButtonClick={() => navigate(-1)}
            sx={{ textTransform: "capitalize" }}
          />
          <ActionButton
            buttonText="submit"
            onButtonClick={handleSaveRole}
            disabled={loading || !selectedPermissions.some((p) => p.active)}
            sx={{ textTransform: "capitalize" }}
          />
        </Grid2>
      </CardLayout>
    </>
  );
};
